// "Le Journal de votre PEA": the daily report laid out as a newspaper front
// page. The markdown is cut into articles by code (no model call): code
// notices (> quotes), tables and the report title are left out, inline
// markdown is flattened to plain text.

export const MAX_ARTICLE_CHARS = 900;

export const plainText = (s) => String(s || '')
  .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
  .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/(^|[\s(])\*([^*\s][^*]*)\*/g, '$1$2')
  .replace(/`([^`]+)`/g, '$1')
  .replace(/\s+/g, ' ')
  .trim();

const stripNumber = (title) => plainText(title).replace(/^\d+[.)]\s*/, '');

// parseReport -> [{ title, blocks: [{ type: 'p' | 'li' | 'h', text }] }]
export const parseReport = (markdown) => {
  const sections = [];
  let current = null;
  let paragraph = [];
  const flush = () => {
    if (paragraph.length && current) current.blocks.push({ type: 'p', text: plainText(paragraph.join(' ')) });
    paragraph = [];
  };
  String(markdown || '').split('\n').forEach((raw) => {
    const line = raw.trim();
    if (!line || line.startsWith('>') || line.startsWith('|') || /^[-*_]{3,}$/.test(line)) {
      flush();
      return;
    }
    if (/^#\s/.test(line)) { flush(); return; }
    const h2 = /^##\s+(.*)$/.exec(line);
    if (h2) {
      flush();
      current = { title: stripNumber(h2[1]), blocks: [] };
      sections.push(current);
      return;
    }
    if (!current) return; // meta lines before the first section
    const h3 = /^#{3,}\s+(.*)$/.exec(line);
    if (h3) { flush(); current.blocks.push({ type: 'h', text: plainText(h3[1]) }); return; }
    const li = /^(?:[-*+]|\d+[.)])\s+(.*)$/.exec(line);
    if (li) { flush(); current.blocks.push({ type: 'li', text: plainText(li[1]) }); return; }
    paragraph.push(line);
  });
  flush();
  return sections.filter((s) => s.blocks.length > 0);
};

// Keeps the first blocks of an article within a character budget.
export const trimBlocks = (blocks, budget = MAX_ARTICLE_CHARS) => {
  const out = [];
  let used = 0;
  for (const b of blocks) {
    if (used >= budget) break;
    const room = budget - used;
    const text = b.text.length > room && b.type !== 'h' ? `${b.text.slice(0, room).replace(/\s+\S*$/, '')}…` : b.text;
    out.push({ ...b, text });
    used += text.length;
  }
  return { blocks: out, truncated: out.length < blocks.length || out.some((b, i) => b.text !== blocks[i].text) };
};

export const digestLines = (report) => String(report?.digest || '').split('\n').map((l) => l.trim()).filter(Boolean);

// The front page: headline, standfirst, briefs, the lead article and the
// other articles.
export const frontPage = (report) => {
  const sections = parseReport(report?.content);
  const digest = digestLines(report);
  const lead = sections[0];
  const firstText = lead?.blocks.find((b) => b.type !== 'h')?.text || '';
  const headline = digest[0] || firstText.split(/(?<=[.!?])\s/)[0] || report?.subject || 'Votre rapport du jour';
  return {
    headline,
    standfirst: digest[1] || '',
    briefs: digest.slice(2),
    lead: lead ? { title: lead.title, ...trimBlocks(lead.blocks, 1100) } : null,
    articles: sections.slice(1).map((s) => ({ title: s.title, ...trimBlocks(s.blocks) })),
  };
};

const dayOfYear = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 86400000) + 1;
};

export const issueNumber = (iso) => dayOfYear(iso);
